import React, { createContext, useContext, ReactElement, FC } from 'react';

import { decorate, observable, computed } from 'mobx';
import { useLocalStore } from 'mobx-react-lite';

import { isEmpty, without } from 'lodash';
import { isNullOrUndefined } from 'util';
import * as uuid from 'uuid';

import {
  OnlineOrder,
  OrderingCategory,
  OrderItem,
  OrderingItem,
  BaseOrderingStore,
  OrderingModifier,
  OnlineOrderDeliverySettings,
  Promotion,
  PromoCode,
  DeliveryQuote,
  OnlineUser,
  ResultResponse,
  OnlineOrderingStore
} from 'kiolyn-api';

import { ViewState } from '../domain/models/ViewState';
import IOrderingRepository from '../domain/repositories/IOrderingRepository';
import newOrder from '../domain/usecases/NewOrder';
import newOrderItem from '../domain/usecases/NewOrderItem';
import loadOrderItemSelectableModifiers from '../domain/usecases/LoadOrderItemSelectableModifiers';
import updateOrderCalculatedValues from '../domain/usecases/UpdateOrderCalculatedValues';
import addUnitToAddress from '../domain/usecases/AddUnitToAddress';
import IDeliveryRepository from '../domain/repositories/IDeliveryRepository';
import getAvailablePromotions from '../domain/usecases/GetAvailablePromotions';
import applyCheapestPromotion from '../domain/usecases/ApplyCheapestPromotion';
import updateAppliedPromotions from '../domain/usecases/UpdateAppliedPromotions';
import applyBuyStGetStPromotion from '../domain/usecases/ApplyBuyStGetStPromotion';
import { OnlineOrderType } from '../domain/models/CommonTypes';
import moment from 'moment-timezone';
import {
  defaultTimezone,
  ooV2Url,
  timestampFormat
} from '../domain/models/Constants';
import validateBizHours from '../domain/usecases/ValidateBizHour';
import IAuthRepository from '../domain/repositories/IAuthRepository';
import newUser from '../domain/usecases/NewUser';
import calculateCategorySchedules from '../domain/usecases/CalculateCategorySchedules';
import {
  SaveOrderAsyncViewState,
  SaveOrderViewState
} from '../domain/models/SaveOrderViewState';

interface SubmitOrderResult {
  version: '1' | '2';
  status: 'error' | 'success' | 'awaiting_payment' | 'paying';
  message?: string;
  orderId?: string;
  deliveryDropoffUtc?: string;
}

export class OrderingStore {
  // The main screen view state
  viewState = ViewState.OK;

  orderingRepository?: IOrderingRepository;
  deliveryRepository?: IDeliveryRepository;
  authRepository?: IAuthRepository;

  store: BaseOrderingStore & { timezone?: string } & OnlineOrderingStore & {
      category_types?: string[];
    } = {};
  order: OnlineOrder = newOrder();
  onlineUser: OnlineUser = newUser();
  availablePromotions: Promotion[] = [];

  submitToken: string = '';
  shouldRefreshSubmitToken: boolean = false;
  isSendEmail: boolean = true;

  // Current being editted Order Item
  editingOrderItem?: OrderItem;

  submitState: SaveOrderViewState = ViewState.NONE;
  submitErrorMessage?: string;
  submitError?: {
    detail?: string;
    stack?: string;
  };

  deliveryQuoteState = ViewState.OK;
  isNoDriverError = false;
  deliveryDropoffIsoDateTime: string = '';

  websocket?: EventSource;
  ws?: WebSocket;
  payingTimeout?: NodeJS.Timeout;
  isSelectPaymentMethod?: boolean;

  constructor(
    orderingRepository?: IOrderingRepository,
    deliveryRepository?: IDeliveryRepository,
    authRepository?: IAuthRepository,
    store?: BaseOrderingStore,
    order?: OnlineOrder,
    onlineUser?: OnlineUser,
    submitState?: ViewState
  ) {
    this.orderingRepository = orderingRepository;
    this.deliveryRepository = deliveryRepository;
    this.authRepository = authRepository;
    if (!isNullOrUndefined(store)) {
      this.store = store;
    }
    if (!isNullOrUndefined(order)) {
      this.order = order;
    }
    if (!isNullOrUndefined(onlineUser)) {
      this.onlineUser = onlineUser;
    }
    if (!isNullOrUndefined(submitState)) {
      this.submitState = submitState;
    }

    // Token expire in 2 minutes, so refresh every 1.5 minutes
    setInterval(() => this.refreshSubmitToken(), 60 * 1000);
  }

  private refreshSubmitToken() {
    this.shouldRefreshSubmitToken = !this.shouldRefreshSubmitToken;
  }

  // Return all categories available for ordering
  get categories(): OrderingCategory[] {
    if (isNullOrUndefined(this.store)) {
      return [];
    }
    return this.store.categories || [];
  }

  get modifiers(): OrderingModifier[] {
    if (isNullOrUndefined(this.store)) {
      return [];
    }
    return this.store.modifiers || [];
  }

  // The current Modifiers list, calculatd from the current editing Order Item
  get editingModifiers(): OrderingModifier[] {
    if (
      isNullOrUndefined(this.editingOrderItem) ||
      isNullOrUndefined(this.categories) ||
      isNullOrUndefined(this.modifiers)
    ) {
      return [];
    }
    return loadOrderItemSelectableModifiers(
      this.editingOrderItem,
      this.categories,
      this.modifiers
    );
  }

  get promotions(): Promotion[] {
    if (this.store === undefined) {
      return [];
    }
    return this.store.promotions || [];
  }

  // Reload the whole menu based on given storeid
  public async reload(): Promise<void> {
    this.refreshSubmitToken();
    if (isNullOrUndefined(this.orderingRepository)) {
      return;
    }

    this.viewState = ViewState.LOADING;
    try {
      const store = await this.orderingRepository.loadStore();

      if (isNullOrUndefined(store)) {
        console.error('Could not load store.');
        this.viewState = ViewState.ERROR;
        return;
      }

      if ((store as any).version === 'v2') {
        const pathName = window.location.pathname;
        const query = window.location.search;
        window.location.replace(`${ooV2Url}${pathName}${query}`);
        return;
      }

      // Keep as local property
      this.store = store;

      // Create a new Order ready for putting item in
      this.order = newOrder(this.store, this.order);
      this.viewState = ViewState.OK;
    } catch (e) {
      console.error(e);
      this.viewState = ViewState.ERROR;
    }
  }

  // Handling an item got selected
  public createItem(category: OrderingCategory, item: OrderingItem) {
    this.editingOrderItem = newOrderItem(category, item);
  }

  public editItem(item: OrderItem) {
    this.editingOrderItem = item;
  }

  public deleteItem(item: OrderItem) {
    this.order.items = without(this.order.items, item);
    // Keep the deleted item to re-check promotion
    this.onOrderItemUpdate(undefined, item);
  }

  /// Finish editing by clearing the editing order item
  /// and apply any changes plus recalculate every calculated values
  public finishEditing(update: boolean) {
    if (isNullOrUndefined(this.editingOrderItem)) {
      return;
    }
    if (update === true) {
      if (!this.order.items.includes(this.editingOrderItem)) {
        this.order.items.push(this.editingOrderItem);
      }
      this.onOrderItemUpdate(this.editingOrderItem);
    }
    this.editingOrderItem = undefined;
  }

  public async updateOrderAmounts() {
    this.order = updateOrderCalculatedValues(this.order, this.store);
  }

  public updateAvailablePromotions() {
    this.availablePromotions = getAvailablePromotions(
      this.order,
      this.store?.promotions
    );
  }

  public onOrderItemUpdate(item?: OrderItem, deletedItem?: OrderItem) {
    updateAppliedPromotions(this.order, this.promotions, deletedItem);
    if (item !== undefined) {
      this.applyBuyStGetStPromotion();
    }
    this.updateAvailablePromotions();
    this.updateOrderAmounts();
  }

  public applyCheapestPromotion(promotion: Promotion): void {
    applyCheapestPromotion(promotion, this.order.items);
    this.updateAvailablePromotions();
    this.updateOrderAmounts();
  }

  public applyBuyStGetStPromotion(): void {
    applyBuyStGetStPromotion(this.availablePromotions, this.order.items);
  }

  public async signUpAccount(
    user: OnlineUser
  ): Promise<ResultResponse | undefined> {
    const res = await this.authRepository?.signUp(user, this.store.id || '');
    return res;
  }

  public async signInAccount(
    user: OnlineUser
  ): Promise<OnlineUser | undefined> {
    return await this.authRepository?.signIn(user);
  }

  public async submitCurrentOrder(
    ignorePayment: boolean,
    tableId?: string
  ): Promise<void> {
    this.refreshSubmitToken();
    this.isNoDriverError = false;
    try {
      if (isNullOrUndefined(this.orderingRepository)) {
        throw new Error('Repository is not available');
      }

      const isChangeOrderByTime = this.checkOrderByTimeChange();
      if (isChangeOrderByTime) {
        this.submitErrorMessage =
          'Sorry! Some of the goods in your order are currently unavailable. Please recheck before submitting again.';
        this.submitState = ViewState.ERROR;
        return;
      }

      //Combine to customer full name:
      this.order.customer_name =
        this.order.customer_first_name + ' ' + this.order.customer_last_name;

      this.deliveryDropoffIsoDateTime = '';
      this.submitErrorMessage = '';
      const onlineStore = (this.store as any) as OnlineOrderingStore;
      if (onlineStore.sync_handle_order) {
        this.submitState = SaveOrderAsyncViewState.PAYING;
      } else {
        this.submitState = ViewState.LOADING;
      }

      // combine manual address
      if (this.order.billing_street && !this.order.billing_address) {
        this.order.billing_address = `${this.order.billing_street}, ${this.order.billing_city}, ${this.order.billing_state}`;
      }

      const pickupTime =
        this.order.time ??
        moment()
          .add(this.getOrderDelayTime(), 'minutes')
          .tz(this.store.timezone || defaultTimezone)
          .format(timestampFormat);
      const validate = this.validateBizHoursPickupTime(pickupTime);
      if (validate !== null) {
        this.submitErrorMessage =
          'Store is closed at pickup time. Please select another pickup time.';
        this.submitState = ViewState.ERROR;
        return;
      }

      this.order.customer_address = addUnitToAddress(
        this.order.customer_place?.description ?? '',
        this.order.customer_place_unit ?? ''
      );
      if (!this.order.unique_id) {
        this.order.unique_id = uuid.v4();
      }
      this.order.table = tableId;

      if (this.order.order_type !== 'delivery') {
        this.order.default_tip_for_driver = undefined;
        this.order.tip_for_driver = undefined;
        this.order.tip_for_restaurant_staff = undefined;
      }

      const order = this.updateOrderTime();
      // const deliverySettings = (this.store as any)
      //   .delivery_settings as OnlineOrderDeliverySettings;
      // const deliveryType = deliverySettings?.delivery_type ?? '';

      // set flag is_catering
      const formatDate = 'MM-DD-YYYY';
      let isCatering =
        (!order.is_asap &&
          moment(order.timeIsoFormat)
            .format(formatDate)
            .toString() !==
            moment()
              .format(formatDate)
              .toString() &&
          onlineStore.online_order_catering_settings?.enable_online_catering) ||
        false;
      order.is_catering = isCatering;

      const res = await this.orderingRepository.submitOrder(
        order,
        this.store,
        this.submitToken,
        ignorePayment
      );
      if ((res as any).version === '2') {
        return this.handleSubmitOrderResultV2(
          res as SubmitOrderResult,
          ignorePayment
        );
      }

      if (res.result === true) {
        if (onlineStore.sync_handle_order || order.is_payment_by_apple_pay) {
          const orderId = (res as any).info?.channelId;
          if (orderId !== undefined) {
            this.order.id = orderId;
            return this.connectToOrderHandler(orderId, ignorePayment);
          }
          return;
        }

        this.handleSubmitSuccess(res, ignorePayment);
      } else {
        this.handleSubmitFailed(res);
      }
    } catch (e) {
      if (e.response?.data?.type === 'couriers_busy') {
        this.isNoDriverError = true;
      }
      this.submitErrorMessage = 'Error submitting order, please retry later.';
      this.submitError = e.response?.data;
      const errorMessage = this.submitError?.detail;
      if (
        errorMessage !== undefined &&
        !isEmpty(errorMessage) &&
        errorMessage !== '{}'
      ) {
        this.submitErrorMessage = errorMessage;
      }
      this.submitState = ViewState.ERROR;
    }
  }

  private handleSubmitOrderResultV2(
    res: SubmitOrderResult,
    ignorePayment: boolean
  ): void {
    const { status, message, orderId } = res;
    if (status === 'success') {
      this.handleSubmitSuccess(res, ignorePayment);
    } else if (status === 'paying') {
      if (this.submitState === SaveOrderAsyncViewState.AWAITING_PAYMENT) {
        this.submitState = SaveOrderAsyncViewState.PAYING;
      } else if (isEmpty(orderId)) {
        this.submitState = ViewState.ERROR;
        this.submitErrorMessage = `Invalid server response. Missing order id.`;
      } else {
        this.order.id = orderId ?? '';
        this.submitState = SaveOrderAsyncViewState.PAYING;
        this.listenForPayingStatus(orderId ?? '', ignorePayment);
      }
    } else if (status === 'awaiting_payment') {
      if (isEmpty(orderId)) {
        this.submitState = ViewState.ERROR;
        this.submitErrorMessage = `Invalid server response. Missing order id.`;
      } else {
        this.order.id = orderId ?? '';
        this.submitState = SaveOrderAsyncViewState.AWAITING_PAYMENT;
        this.listenForPayingStatus(orderId ?? '', ignorePayment);
      }
    } else {
      this.submitState = ViewState.ERROR;
      this.submitErrorMessage = message;
    }
  }

  public listenForPayingStatus(orderId: string, ignorePayment: boolean): void {
    if (this.orderingRepository === undefined) {
      return;
    }

    if (this.payingTimeout) {
      clearTimeout(this.payingTimeout);
    }

    console.debug(`[WS] connecting to order status endpoint for ${orderId}`);
    this.ws = new WebSocket(
      process.env.REACT_APP_STAGE === 'production'
        ? 'wss://wsapi-production.kiolyn.com'
        : 'wss://wsapi-staging.kiolyn.com'
    );
    this.ws.onopen = () => {
      console.debug(`[WS] connected to order status endpoint for ${orderId}`);
      this.ws?.send(JSON.stringify({ action: 'addClient', taskId: orderId }));
      this.payingTimeout = setTimeout(async () => {
        await this.recheckOrderStatus(this.order.unique_id, ignorePayment);
        this.ws?.close();
      }, 2 * 60 * 1000);
    };

    this.ws.onerror = async err => {
      console.error(`[WS] error: ${err.type}`);
      this.ws?.close();
    };

    this.ws.onclose = async () => {
      console.debug(`[WS] closed`);
    };

    this.ws.onmessage = async message => {
      console.debug(`[WS] message received: ${message.data}`);
      if (message.data === undefined || message.data === null) return;

      const res = JSON.parse(message.data) as any;
      if (res.type !== 'paymentResult') return;

      this.handleSubmitOrderResultV2(res as SubmitOrderResult, ignorePayment);
      if (res.status === 'success' || res.status === 'error') {
        this.ws?.close();
      }
    };
  }

  private connectToOrderHandler(
    channelId: string,
    ignorePayment: boolean
  ): void {
    if (this.orderingRepository === undefined) {
      return;
    }

    console.log('Connecting..');
    this.websocket = this.orderingRepository.getWebsocketForSubmitOrder(
      channelId
    );

    this.websocket.onopen = () => {
      console.log('Sync with order handler successfully');
      setTimeout(async () => {
        await this.recheckOrderStatus(this.order.unique_id, ignorePayment);
        this.websocket?.close();
      }, 120000);
    };

    // websocket onerror event listener
    this.websocket.onerror = async () => {
      this.websocket?.close();
      await this.recheckOrderStatus(this.order.unique_id, ignorePayment);
    };

    this.websocket.onmessage = async message => {
      if (message.data) {
        const res = JSON.parse(message.data);
        if (
          res.result === false &&
          res.message &&
          res.message.toLocaleLowerCase().includes('timeout')
        ) {
          await this.recheckOrderStatus(this.order.unique_id, ignorePayment);
          this.websocket?.close();
        } else {
          this.handleSubmitResult(res, ignorePayment);
          this.websocket?.close();
        }
      }
    };
  }

  private async recheckOrderStatus(
    orderUniqueId: string | undefined,
    ignorePayment: boolean,
    errorConnect?: boolean
  ) {
    if (this.submitState === SaveOrderAsyncViewState.PAYING) {
      if (orderUniqueId === undefined) {
        console.error('Unique ID is undefined');
        return;
      }

      const orderStatus = await this.orderingRepository?.checkOrderStatus(
        orderUniqueId
      );
      if (orderStatus === true) {
        this.handleSubmitSuccess(undefined, ignorePayment);

        return true;
      } else if (!errorConnect) {
        this.submitState = SaveOrderAsyncViewState.WAITINGFAILED;
      }
    }
  }

  private handleSubmitResult(
    res: ResultResponse,
    ignorePayment: boolean
  ): void {
    if (res.result === true) {
      this.handleSubmitSuccess(res, ignorePayment);
    } else {
      this.handleSubmitFailed(res);
    }
  }

  private handleSubmitSuccess(
    res: ResultResponse | undefined,
    ignorePayment: boolean
  ): void {
    // If submit OK, then clear the current and create a new one ready for next ordering
    if (res !== undefined) {
      if (res.message === 'Cannot send Email!') {
        this.isSendEmail = false;
      }
      if ('delivery_iso_dt' in res) {
        this.deliveryDropoffIsoDateTime = res['delivery_iso_dt'] as string;
      }
    }

    let type = this.order.order_type as string;
    let email = this.order.customer_email as string;
    this.order = newOrder(this.store, this.order);
    this.order.type = type;
    this.order.customer_email = email;
    this.resetPromotion();

    if (ignorePayment) {
      this.submitState = ViewState.SUBMITTED;
    } else {
      this.submitState = ViewState.OK;
    }
  }

  private handleSubmitFailed(res: ResultResponse): void {
    if (res.message?.includes('ERROR:PRINTFAILED')) {
      this.submitState = ViewState.PRINTFAILED;
    } else {
      this.submitErrorMessage = res.message;
      this.submitState = ViewState.ERROR;
    }
  }

  public async applyPromoCode(name: string): Promise<void> {
    if (name) {
      this.order.discount = await this.orderingRepository?.getPromoCodeByName(
        name
      );
      this.order = updateOrderCalculatedValues({ ...this.order }, this.store);
    }
  }

  public setPromoCode(promoCode: PromoCode): void {
    if (promoCode) {
      this.order.discount = promoCode;
      this.order = updateOrderCalculatedValues({ ...this.order }, this.store);
    }
  }

  public async createDeliveryQuote(
    destination: string,
    deliveryIsoTime?: string,
    orderValue?: number
  ): Promise<DeliveryQuote> {
    const settings = (this.store as any)
      .delivery_settings as OnlineOrderDeliverySettings;
    const origin = settings?.delivery_from_address?.description;
    const storeId = this.store.id;
    if (storeId === undefined || storeId === '' || !this.deliveryRepository) {
      throw new Error('Invalid store');
    }
    if (origin === undefined || origin === '') {
      throw new Error('Store address is not configured');
    }
    if (destination === '') {
      throw new Error('Invalid delivery destination');
    }

    this.deliveryQuoteState = ViewState.LOADING;
    try {
      return await this.deliveryRepository.getQuote(
        storeId,
        origin,
        destination,
        deliveryIsoTime,
        orderValue
      );
    } finally {
      this.deliveryQuoteState = ViewState.OK;
    }
  }

  public setOrderType(type: OnlineOrderType) {
    this.order.order_type = type;
    this.updateOrderAmounts();
  }

  public setEstimatedDeliveryFeeAndDuration(
    fee: number,
    time: string,
    quoteId: string
  ) {
    this.order.delivery_fee_amount = fee;
    this.order.quote_id = quoteId;
    this.updateOrderAmounts();

    if (this.order.is_asap) {
      this.order.delivery_quote_duration = moment(time).diff(
        moment(),
        'minutes'
      );
    }
  }

  public getOrderDelayTime(): number {
    let delay;
    if (this.order.order_type === 'delivery') {
      delay = this.store.delay_delivery_time;
    }
    if (this.order.order_type === 'pickup') {
      delay = this.store.delay_pickup_time;
    }
    if (this.order.order_type === 'dinein') {
      delay = this.store.delay_dinein_time;
    }
    return delay ?? 20;
  }

  public getOrderDeliveryTime(): number {
    if (
      this.order.order_type !== 'delivery' ||
      !('delivery_settings' in this.store)
    ) {
      return 0;
    }

    const settings = (this.store as any)
      .delivery_settings as OnlineOrderDeliverySettings;

    return this.order.delivery_quote_duration ?? settings.delivery_time ?? 20;
  }

  public validateBizHoursPickupTime(value?: string): string | null {
    if (isEmpty(value)) {
      return null;
    }
    let pickupTime = moment(value, timestampFormat).tz(
      this.store.timezone || defaultTimezone,
      true
    );
    if (this.order.order_type === 'delivery') {
      pickupTime.add(-this.getOrderDeliveryTime(), 'minutes');
    }

    let preparationTime = pickupTime
      .clone()
      .add(-this.getOrderDelayTime(), 'minutes');

    return (
      validateBizHours(
        timestampFormat,
        this.store.biz_hours,
        pickupTime,
        this.store.holidays,
        this.store.timezone
      ) ||
      validateBizHours(
        timestampFormat,
        this.store.biz_hours,
        preparationTime,
        this.store.holidays,
        this.store.timezone
      )
    );
  }

  public checkOrderByTimeChange(): boolean {
    const itemCount = this.order.items.length;
    for (let i = 0; i < this.order.items.length; i++) {
      const item = this.order.items[i];
      const category = this.store.categories?.find(
        cat => cat.id === item.categoryid
      );
      const isAvailable =
        calculateCategorySchedules(
          category,
          this.order,
          this.getOrderDelayTime(),
          this.getOrderDeliveryTime()
        ) === '';

      if (!isAvailable) {
        this.order.items.splice(i, 1);
        i--;
      }
    }

    if (this.order.items.length !== itemCount) {
      this.updateOrderAmounts();
      return true;
    }

    return false;
  }

  private resetPromotion(): void {
    this.store.promotions?.forEach(promotion => (promotion.applied_count = 0));
  }

  private updateOrderTime(): OnlineOrder {
    // Set order.time to send to PC if it's delivery
    if (!isEmpty(this.order.time)) {
      return this.order;
    }

    const order = { ...this.order };
    const orderTime = moment().add(
      this.getOrderDelayTime() + this.getOrderDeliveryTime(),
      'minutes'
    );
    order.time = orderTime
      .clone()
      .tz(this.store.timezone || defaultTimezone)
      .format(timestampFormat);
    order.timeIsoFormat = orderTime.format();

    return order;
  }
}

decorate(OrderingStore, {
  viewState: observable,
  store: observable,
  order: observable,
  submitToken: observable,
  shouldRefreshSubmitToken: observable,
  editingOrderItem: observable,
  categories: computed,
  editingModifiers: computed,
  promotions: computed,
  availablePromotions: observable,
  submitState: observable,
  submitErrorMessage: observable,
  deliveryQuoteState: observable,
  deliveryDropoffIsoDateTime: observable
});

interface OrderingProviderProps {
  children: ReactElement;
  orderingRepository?: IOrderingRepository;
  deliveryRepository?: IDeliveryRepository;
  authRepository?: IAuthRepository;
  store?: BaseOrderingStore;
  order?: OnlineOrder;
  onlineUser?: OnlineUser;
  submitState?: ViewState;
  isIgnoreLoadStore?: boolean;
}

export const OrderingProvider: FC<OrderingProviderProps> = ({
  children,
  orderingRepository,
  deliveryRepository,
  authRepository,
  store,
  order,
  onlineUser,
  submitState,
  isIgnoreLoadStore
}: OrderingProviderProps) => {
  const orderingStore = useLocalStore(
    () =>
      new OrderingStore(
        orderingRepository,
        deliveryRepository,
        authRepository,
        store,
        order,
        onlineUser,
        submitState
      )
  );
  !isIgnoreLoadStore && orderingStore.reload();
  return (
    <OrderingContext.Provider value={orderingStore}>
      {children}
    </OrderingContext.Provider>
  );
};

export const OrderingContext = createContext(new OrderingStore());
export const useOrdering = () => useContext(OrderingContext);
